/**
 * The account's tabs: Overview · Bills · Contacts (Customer 360, decision 6; the
 * owner's polish of 24 Oct 2026). Activity is not one of them: the rail keeps the
 * recent activity on screen beside every tab, and "View full activity" opens the
 * whole history at its own address — one home for it, not two. Each tab is a page
 * with its own address, so the tabs are links marked `aria-current="page"` — not
 * an ARIA tab list — with the one underline that slides from tab to tab, as
 * everywhere in the app (01 §3.2). They carry the list's own state
 * (`?from=today…`), so Back and the walk keep working from any tab. No Ledger: it
 * has no tab, no route and no placeholder until Phase 2.
 */
import { Link, useLocation } from 'react-router-dom';
import { cx, useIndicator } from '@timely/ui';
import { useActiveWorkspace } from '../../workspace/WorkspaceProvider';
import { paths, type AccountTab } from '../../workspace/navigation';
import { useAccount } from './accountPage';

/** The address of one of the account's tabs, keeping the list's own state, so Back and the walk work from any tab. */
export function useTabTo(): (tab: AccountTab) => string {
    const page = useAccount();
    const { workspace } = useActiveWorkspace();
    const { search } = useLocation();
    return tab => `${paths(workspace.slug).customerTab(page.customer.id, tab)}${search}`;
}

export function AccountTabs({ current }: { current: AccountTab }) {
    const page = useAccount();
    const tabTo = useTabTo();
    const { track, style, shown, animate } = useIndicator<HTMLElement>(current);
    const statement = page.statement.statement;
    const people = (page.customer.contactPerson || page.customer.contactNumber ? 1 : 0) + (page.customer.additionalContacts?.length ?? 0);
    const tabs: { value: AccountTab; label: string; count?: number }[] = [
        { value: 'overview', label: 'Overview' },
        { value: 'bills', label: 'Bills', ...(page.source === 'tally' && statement?.billWise ? { count: statement.bills.length } : {}) },
        { value: 'contacts', label: 'Contacts', ...(people > 0 ? { count: people } : {}) },
    ];
    return (
        <nav ref={track} aria-label="This account" className="relative flex items-end gap-1 border-b border-separator overflow-x-auto">
            {shown && (
                <span aria-hidden="true" data-indicator="" style={style}
                    className={cx('absolute left-0 bottom-0 h-0.5 rounded-full bg-accent', animate && 'transition-[transform,width] duration-200 ease-out-quart')} />
            )}
            {tabs.map(t => {
                const on = t.value === current;
                return (
                    <Link key={t.value} to={tabTo(t.value)} data-active={on}
                        aria-current={on ? 'page' : undefined}
                        className={cx('relative inline-flex items-center gap-2 h-11 px-3 text-body font-semibold whitespace-nowrap transition-colors duration-150',
                            on ? 'text-label' : 'text-label-3 hover:text-label')}>
                        {t.label}
                        {t.count !== undefined && <span className="num text-secondary font-medium text-label-3">{t.count}</span>}
                    </Link>
                );
            })}
        </nav>
    );
}
